import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useBooking } from "../context/BookingContext";
import { useLang } from "../context/LanguageContext";
import { fmtNumber, fmtPrice } from "../lib/api";

const ListingCard = ({ listing, index = 0 }) => {
  const { openBooking } = useBooking();
  const { t } = useLang();
  const forSale = listing.status === "for_sale";

  return (
    <motion.article
      data-testid={`property-card-${listing.id}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay: (index % 3) * 0.09, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col border border-engraved bg-white shadow-elevated transition-all duration-500 hover:-translate-y-1.5 hover:shadow-lifted"
    >
      <Link to={`/listing/${listing.id}`} className="relative block overflow-hidden">
        <img
          src={listing.images?.[0]}
          alt={listing.title}
          loading="lazy"
          className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <span
          className={`absolute left-4 top-4 px-3 py-1.5 font-cinzel text-[10px] font-bold uppercase tracking-[0.2em] ${
            forSale ? "bg-crimson text-paper" : listing.status === "pending" ? "bg-gold text-navy" : "bg-navy text-paper"
          }`}
        >
          {forSale ? t("card.forSale") : listing.status === "pending" ? t("card.pending") : t("card.sold")}
        </span>
        {listing.tag && (
          <span className="absolute bottom-4 left-4 bg-paper/95 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-navy">
            {listing.tag}
          </span>
        )}
        {listing.open_house && (
          <span data-testid={`property-card-open-house-${listing.id}`} className="absolute bottom-4 right-4 bg-gold px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.15em] text-navy">
            {t("oh.label")}
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-6">
        <p className="font-serif text-2xl font-bold text-navy">{fmtPrice(listing.price)}</p>
        <Link to={`/listing/${listing.id}`} className="mt-1 transition-colors hover:text-crimson">
          <p className="text-sm font-medium text-ink">{listing.address}</p>
          <p className="text-xs text-slate-500">
            {listing.city}, {listing.state} {listing.zip}
          </p>
        </Link>
        {listing.beds != null && listing.sqft != null && (
          <p className="mt-4 border-t border-engraved pt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-600">
            {listing.beds} BD · {listing.baths} BA · {fmtNumber(listing.sqft)} SF
          </p>
        )}
        <div className="mt-5 flex items-center justify-between gap-3 pt-1">
          <Link
            to={`/listing/${listing.id}`}
            data-testid={`property-card-view-${listing.id}`}
            className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy underline-offset-4 hover:underline"
          >
            {t("card.view")}
          </Link>
          {forSale && (
            <button
              data-testid={`property-card-book-button-${listing.id}`}
              onClick={() => openBooking({ listing, service: "tour" })}
              className="bg-navy px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-crimson"
            >
              {t("card.tour")}
            </button>
          )}
        </div>
      </div>
    </motion.article>
  );
};

export default ListingCard;
